import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Alert, Avatar, Box, Button, Card, CardContent, CardHeader, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, IconButton,
  Link, List, ListItem, ListItemText, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TableSortLabel, Tooltip, Typography,
} from '@mui/material';
import FormProvider from '../form/FormProvider';
import TextField from '../form/TextField';
import SelectField from '../form/SelectField';
import EditIcon from '@mui/icons-material/Edit';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AddOfficeModal from './AddOfficeModal';
import ApproveOfficeModal from './ApproveOfficeModal';
import ApproveUserModal from './ApproveUserModal';
import EditOfficeModal from './EditOfficeModal';
import EditUserModal from './EditUserModal';
import InviteUserModal from './InviteUserModal';
import StatusBadge, { BROKER_STATUS_MAP, USER_STATUS_MAP } from '../shared/StatusBadge';
import { Roles } from '../../constants';

const formatAddress = (addr) => {
  if (!addr) return null;
  const parts = [addr.address1, addr.address2, [addr.city, addr.state].filter(Boolean).join(', '), addr.postalCode].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
};

import SharedDataItem from '../shared/DataItem';

const DataItem = (props) => <SharedDataItem sx={{ mb: 1, py: 0 }} {...props} />;

const BrokerCompanyDetail = ({
  broker, offices = [], users = [], mode = 'lender',
  onToggleStatus, onUpdateCompany, onAddOffice, onInviteUser, onApproveUser,
  onEditUser, onDeleteUser, onResendInvite, onPreApprove, onApproveOffice,
  onEditOffice, canEditNmls = true, backLink, accountExecutives = [],
  fileContactConflict, onFileContactConflictResolve, onFileContactConflictDismiss,
}) => {
  const isBrokerMode = mode === 'broker';
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showAddOfficeModal, setShowAddOfficeModal] = useState(false);
  const [approveUser, setApproveUser] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [approveOffice, setApproveOffice] = useState(null);
  const [editOffice, setEditOffice] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [resendingUserId, setResendingUserId] = useState(null);
  const [showPreApproveModal, setShowPreApproveModal] = useState(false);
  const [preApproving, setPreApproving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const hasBrokerAdmin = users.some(u => u.role === Roles.BrokerAdmin);
  const isPreApprovedOrActive = broker.status === 'Pre-Approved' || broker.status === 'Active';
  const inviteRole = hasBrokerAdmin ? Roles.Broker : Roles.BrokerAdmin;
  const showInviteButton = onInviteUser && (isBrokerMode || !hasBrokerAdmin || isPreApprovedOrActive);
  const [saving, setSaving] = useState(false);
  const [resolvingConflict, setResolvingConflict] = useState(false);
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedUsers = useMemo(() => {
    const copy = [...users];
    copy.sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'name': aVal = `${a.firstName} ${a.lastName}`.toLowerCase(); bVal = `${b.firstName} ${b.lastName}`.toLowerCase(); break;
        case 'email': aVal = (a.emailAddress || '').toLowerCase(); bVal = (b.emailAddress || '').toLowerCase(); break;
        case 'nmls': aVal = a.nmls || ''; bVal = b.nmls || ''; break;
        case 'status': aVal = a.status || ''; bVal = b.status || ''; break;
        default: aVal = ''; bVal = '';
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [users, sortField, sortDir]);

  const aeOptions = useMemo(() => [
    { value: '', label: 'None' },
    ...accountExecutives.map(ae => ({ value: ae.id, label: ae.fullName })),
  ], [accountExecutives]);

  const editSchema = z.object({
    name: z.string().min(1, 'Company name is required'),
    nmls: z.string().optional().default(''),
    compId: z.string().optional().default(''),
    address: z.object({
      address1: z.string().optional().default(''),
      address2: z.string().optional().default(''),
      city: z.string().optional().default(''),
      state: z.string().max(2).optional().default(''),
      postalCode: z.string().optional().default(''),
    }),
    website: z.string().optional().default(''),
    accountExecutiveId: z.string().optional().default(''),
  });

  const editMethods = useForm({
    resolver: zodResolver(editSchema),
    defaultValues: {
      name: broker.name || '', nmls: broker.nmls || '', compId: broker.compId || '',
      address: {
        address1: broker.address?.address1 || '', address2: broker.address?.address2 || '',
        city: broker.address?.city || '', state: broker.address?.state || '',
        postalCode: broker.address?.postalCode || '',
      },
      website: broker.website || '',
      accountExecutiveId: broker.accountExecutiveId || '',
    },
  });

  const handleEditStart = () => {
    editMethods.reset({
      name: broker.name || '', nmls: broker.nmls || '', compId: broker.compId || '',
      address: {
        address1: broker.address?.address1 || '', address2: broker.address?.address2 || '',
        city: broker.address?.city || '', state: broker.address?.state || '',
        postalCode: broker.address?.postalCode || '',
      },
      website: broker.website || '',
      accountExecutiveId: broker.accountExecutiveId || '',
    });
    setIsEditing(true);
  };

  const handleEditSave = async (data) => {
    if (!onUpdateCompany) return;
    setSaving(true);
    try {
      const payload = { ...data, accountExecutiveId: data.accountExecutiveId || null };
      await onUpdateCompany(payload);
      setIsEditing(false);
    } finally { setSaving(false); }
  };

  return (
    <Box>
      {/* Page header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          {backLink && (
            <Typography variant="caption" color="text.secondary">
              <Link href={backLink.to} underline="hover">{backLink.label}</Link>
            </Typography>
          )}
          <Typography variant="h5" fontWeight={700}>{broker.name}</Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1}>
          <StatusBadge status={broker.status} map={BROKER_STATUS_MAP} />
          {!isBrokerMode && onPreApprove && broker.status === 'Pending Approval' && (
            <Button size="small" variant="outlined" startIcon={<CheckIcon />} onClick={() => setShowPreApproveModal(true)}>Pre-Approve</Button>
          )}
          {!isBrokerMode && onToggleStatus && (
            <Button size="small" variant="outlined" color="secondary" onClick={onToggleStatus}>
              {broker.status === 'Active' ? 'Disable' : 'Activate'}
            </Button>
          )}
        </Stack>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Broker Profile Card */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardHeader
              title="Broker Profile"
              titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }}
              action={onUpdateCompany && !isEditing ? <Button size="small" variant="contained" startIcon={<EditIcon />} onClick={handleEditStart}>Edit</Button> : undefined}
            />
            <CardContent>
              {isEditing ? (
                <FormProvider methods={editMethods} onSubmit={handleEditSave}>
                  <TextField name="name" label="Company Name" required size="small" sx={{ mb: 2 }} />
                  <TextField name="nmls" label="NMLS" size="small" disabled={isBrokerMode} helperText={isBrokerMode ? 'NMLS cannot be changed by Broker Admin' : undefined} sx={{ mb: 2 }} />
                  <TextField name="compId" label="Compensation ID" size="small" disabled={isBrokerMode} placeholder="Enter Comp ID for pricing" helperText={isBrokerMode ? 'Comp ID can only be changed by a Lender Admin.' : 'This ID is passed to the pricing engine as the userId.'} sx={{ mb: 2 }} />
                  <TextField name="website" label="Website" type="url" size="small" placeholder="https://example.com" sx={{ mb: 2 }} />
                  {!isBrokerMode && (
                    <SelectField name="accountExecutiveId" label="Account Executive" options={aeOptions} size="small" sx={{ mb: 2 }} />
                  )}
                  <TextField name="address.address1" label="Street Address" size="small" placeholder="Street address" sx={{ mb: 2 }} />
                  <TextField name="address.address2" label="Address Line 2" size="small" placeholder="Suite, unit, etc." sx={{ mb: 2 }} />
                  <Grid container spacing={1.5} sx={{ mb: 2 }}>
                    <Grid size={{ xs: 5 }}><TextField name="address.city" label="City" size="small" /></Grid>
                    <Grid size={{ xs: 3 }}><TextField name="address.state" label="State" size="small" placeholder="CA" inputProps={{ maxLength: 2 }} /></Grid>
                    <Grid size={{ xs: 4 }}><TextField name="address.postalCode" label="Postal Code" size="small" placeholder="12345" /></Grid>
                  </Grid>
                  <Stack direction="row" spacing={1}>
                    <Button type="submit" variant="contained" disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
                    <Button onClick={() => setIsEditing(false)} disabled={saving}>Cancel</Button>
                  </Stack>
                </FormProvider>
              ) : (
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}><DataItem label="NMLS">{broker.nmls}</DataItem></Grid>
                  <Grid size={{ xs: 6 }}><DataItem label="Compensation ID">{broker.compId || '—'}</DataItem></Grid>
                  <Grid size={{ xs: 6 }}><DataItem label="Website">{broker.website ? <Link href={broker.website} target="_blank" rel="noreferrer" underline="hover">{broker.website}</Link> : '—'}</DataItem></Grid>
                  <Grid size={{ xs: 6 }}><DataItem label="Address">{formatAddress(broker.address) || '—'}</DataItem></Grid>
                  <Grid size={{ xs: 6 }}><DataItem label="Created Date">{new Date(broker.dateCreated).toLocaleDateString()}</DataItem></Grid>
                  {!isBrokerMode && (
                    <Grid size={{ xs: 6 }}><DataItem label="Account Executive">{broker.accountExecutiveName || '—'}</DataItem></Grid>
                  )}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Satellite Offices Card */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardHeader
              title={`Satellite Offices (${offices.length})`}
              titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }}
              action={isBrokerMode && onAddOffice ? <Button size="small" variant="contained" startIcon={<AddBusinessIcon />} onClick={() => setShowAddOfficeModal(true)}>Add Office</Button> : undefined}
            />
            <List disablePadding>
              {offices.length === 0 && (
                <ListItem><ListItemText secondary="No satellite offices yet" /></ListItem>
              )}
              {offices.map((office, i) => (
                <ListItem key={office.id} divider={i < offices.length - 1} secondaryAction={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <StatusBadge status={office.status} map={USER_STATUS_MAP}>{office.status}</StatusBadge>
                    {onEditOffice && office.status !== 'Pending Approval' && (
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => setEditOffice(office)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    )}
                    {!isBrokerMode && onApproveOffice && office.status === 'Pending Approval' && (
                      <Button size="small" color="success" variant="outlined" startIcon={<CheckIcon />} onClick={() => setApproveOffice(office)}>Approve</Button>
                    )}
                  </Stack>
                }>
                  <ListItemText
                    primary={<Typography variant="body2" fontWeight={600}>{office.name}</Typography>}
                    secondary={
                      <>
                        <Typography variant="caption" color="text.secondary">NMLS: {office.nmls}</Typography>
                        {office.compId && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Comp ID: {office.compId}</Typography>}
                        {formatAddress(office.address) && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{formatAddress(office.address)}</Typography>}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Card>
        </Grid>
      </Grid>

      {/* Users Section */}
      <Card>
        <CardHeader
          title={`Users (${users.length})`}
          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }}
          action={showInviteButton ? <Button size="small" variant="contained" startIcon={<PersonAddIcon />} onClick={() => setShowInviteModal(true)}>{isBrokerMode ? 'Invite User' : hasBrokerAdmin ? 'Invite User' : 'Invite Broker Manager'}</Button> : undefined}
        />
        {users.length === 0 ? (
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <PersonAddIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="h6">No users yet</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {isBrokerMode ? 'Invite users to your company. New users will require Lender Admin approval.' : 'Invite a Broker Manager to get started. They will receive an email to confirm their account.'}
              </Typography>
              {showInviteButton && (
                <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setShowInviteModal(true)}>
                  {isBrokerMode ? 'Invite User' : hasBrokerAdmin ? 'Invite User' : 'Invite Broker Manager'}
                </Button>
              )}
            </Box>
          </CardContent>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={sortField === 'name' ? sortDir : false}>
                    <TableSortLabel active={sortField === 'name'} direction={sortField === 'name' ? sortDir : 'asc'} onClick={() => handleSort('name')}>Name</TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortField === 'email' ? sortDir : false}>
                    <TableSortLabel active={sortField === 'email'} direction={sortField === 'email' ? sortDir : 'asc'} onClick={() => handleSort('email')}>Email</TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortField === 'nmls' ? sortDir : false}>
                    <TableSortLabel active={sortField === 'nmls'} direction={sortField === 'nmls' ? sortDir : 'asc'} onClick={() => handleSort('nmls')}>NMLS</TableSortLabel>
                  </TableCell>
                  {!isBrokerMode && <TableCell>UFF Link</TableCell>}
                  <TableCell sortDirection={sortField === 'status' ? sortDir : false}>
                    <TableSortLabel active={sortField === 'status'} direction={sortField === 'status' ? sortDir : 'asc'} onClick={() => handleSort('status')}>Status</TableSortLabel>
                  </TableCell>
                  {(onEditUser || onDeleteUser || onResendInvite || (!isBrokerMode && onApproveUser)) && <TableCell>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedUsers.map((user) => (
                  <TableRow key={user.userId}>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: 12 }}>{user.firstName?.charAt(0)}{user.lastName?.charAt(0)}</Avatar>
                        <Typography variant="body2" component="span">
                          {user.firstName} {user.lastName}
                          {user.role === Roles.BrokerAdmin && <>&nbsp;<StatusBadge variant="info">Manager</StatusBadge></>}
                          {user.role === Roles.Broker && <>&nbsp;<StatusBadge variant="primary">Loan Officer</StatusBadge></>}
                          {user.role === Roles.Processor && <>&nbsp;<StatusBadge variant="teal">Processor</StatusBadge></>}
                          {user.role === Roles.BrokerFullAccessReadOnly && <>&nbsp;<StatusBadge variant="secondary">Read-only</StatusBadge></>}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell><Typography variant="body2">{user.emailAddress}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{user.nmls || '—'}</Typography></TableCell>
                    {!isBrokerMode && (
                      <TableCell>
                        {user.vestaContactId
                          ? <StatusBadge variant="success" title={user.vestaContactId}>Linked</StatusBadge>
                          : <StatusBadge variant="warning">Not linked</StatusBadge>}
                      </TableCell>
                    )}
                    <TableCell><StatusBadge status={user.status} map={USER_STATUS_MAP}>{user.status}</StatusBadge></TableCell>
                    {(onEditUser || onDeleteUser || onResendInvite || (!isBrokerMode && onApproveUser)) && (
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          {!isBrokerMode && onApproveUser && user.status === 'Pending Approval' && broker.status !== 'Pending Approval' && (
                            <Tooltip title="Approve"><IconButton size="small" color="success" onClick={() => setApproveUser(user)}><CheckIcon fontSize="small" /></IconButton></Tooltip>
                          )}
                          {onResendInvite && user.status === 'Pending Confirmation' && (
                            <Tooltip title="Resend Invite">
                              <IconButton size="small" color="warning" disabled={resendingUserId === user.userId} onClick={async () => { setResendingUserId(user.userId); try { await onResendInvite(user); } finally { setResendingUserId(null); } }}>
                                {resendingUserId === user.userId ? <CircularProgress size={16} /> : <ForwardToInboxIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          )}
                          {onEditUser && (
                            <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => setEditUser(user)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                          )}
                          {onDeleteUser && broker.status !== 'Pending Approval' && (
                            <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleteUser(user)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* ── Modals ── */}
      {showInviteModal && <InviteUserModal mode={mode} role={isBrokerMode ? Roles.Broker : inviteRole} onInvite={onInviteUser} onClose={() => setShowInviteModal(false)} />}
      {editOffice && <EditOfficeModal office={editOffice} canEditNmls={canEditNmls} onSave={onEditOffice} onClose={() => setEditOffice(null)} />}
      {approveOffice && <ApproveOfficeModal office={approveOffice} onApprove={onApproveOffice} onClose={() => setApproveOffice(null)} />}
      {showAddOfficeModal && <AddOfficeModal onAddOffice={onAddOffice} onClose={() => setShowAddOfficeModal(false)} />}
      {approveUser && <ApproveUserModal user={approveUser} onApprove={onApproveUser} onClose={() => setApproveUser(null)} />}
      {editUser && <EditUserModal user={editUser} canEditNmls={canEditNmls} mode={mode} users={users} onSave={onEditUser} onClose={() => setEditUser(null)} />}

      {/* Delete User Dialog */}
      <Dialog open={!!deleteUser} onClose={() => setDeleteUser(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete <strong>{deleteUser?.firstName} {deleteUser?.lastName}</strong>?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>This will deactivate the user. They will no longer be able to log in.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteUser(null)} disabled={deleting}>Cancel</Button>
          <Button variant="contained" color="error" disabled={deleting} onClick={async () => { setDeleting(true); try { await onDeleteUser(deleteUser); setDeleteUser(null); } finally { setDeleting(false); } }}>
            {deleting ? 'Deleting…' : 'Delete User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pre-Approve Dialog */}
      <Dialog open={showPreApproveModal} onClose={() => setShowPreApproveModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>Pre-Approve Broker Company</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 3 }}>
            Pre-approving this broker will send an account activation email to the broker admin. Once they activate their account, they will be able to log in and submit loans.
          </Alert>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Company Details</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 6 }}><DataItem label="Company Name">{broker.name}</DataItem></Grid>
            <Grid size={{ xs: 6 }}><DataItem label="NMLS">{broker.nmls}</DataItem></Grid>
            <Grid size={{ xs: 6 }}><DataItem label="Website">{broker.website ? <Link href={broker.website} target="_blank" rel="noreferrer" underline="hover">{broker.website}</Link> : '—'}</DataItem></Grid>
            <Grid size={{ xs: 6 }}><DataItem label="Address">{formatAddress(broker.address) || '—'}</DataItem></Grid>
            <Grid size={{ xs: 6 }}><DataItem label="Created Date">{new Date(broker.dateCreated).toLocaleDateString()}</DataItem></Grid>
          </Grid>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Broker Admin</Typography>
          {users.filter(u => u.role === Roles.BrokerAdmin).length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow><TableCell>Name</TableCell><TableCell>Email</TableCell><TableCell>NMLS</TableCell></TableRow></TableHead>
                <TableBody>
                  {users.filter(u => u.role === Roles.BrokerAdmin).map(u => (
                    <TableRow key={u.userId}><TableCell>{u.firstName} {u.lastName}</TableCell><TableCell>{u.emailAddress}</TableCell><TableCell>{u.nmls || '—'}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">No broker admin user found.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreApproveModal(false)} disabled={preApproving}>Cancel</Button>
          <Button variant="contained" disabled={preApproving} onClick={async () => { setPreApproving(true); try { await onPreApprove(); setShowPreApproveModal(false); } finally { setPreApproving(false); } }}>
            {preApproving ? 'Pre-Approving…' : 'Pre-Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* File Contact Conflict Dialog */}
      <Dialog open={!!fileContactConflict} onClose={onFileContactConflictDismiss} maxWidth="sm" fullWidth>
        <DialogTitle>File Contact Conflict</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            A file contact already exists with a matching <strong>{fileContactConflict?.matchedField}</strong>: <strong>{fileContactConflict?.matchedValue}</strong>
          </Alert>
          <Typography variant="body2" color="text.secondary">How would you like to proceed?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onFileContactConflictDismiss} disabled={resolvingConflict}>Cancel</Button>
          <Button variant="outlined" disabled={resolvingConflict} onClick={async () => { setResolvingConflict(true); try { await onFileContactConflictResolve('Update'); } finally { setResolvingConflict(false); } }}>
            {resolvingConflict ? 'Updating…' : 'Update Existing'}
          </Button>
          <Button variant="contained" disabled={resolvingConflict} onClick={async () => { setResolvingConflict(true); try { await onFileContactConflictResolve('Insert'); } finally { setResolvingConflict(false); } }}>
            {resolvingConflict ? 'Creating…' : 'Create New'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BrokerCompanyDetail;
